/**
 * AI Classification Review Card
 *
 * Card for a single AI classification entry, used in both
 * the review queue and classification history.
 */
import { useState } from 'react';
import { Card, CardContent, Badge, Button, cn } from '@respectly/ui';
import {
  Brain,
  Check,
  ArrowRight,
  ChevronDown,
  ChevronUp,
  Flag,
  Loader2,
} from 'lucide-react';
import type { PREAiClassification, MessagePurpose } from '@respectly/shared-types';

// ============================================
// Types
// ============================================

interface AIClassificationReviewCardProps {
  classification: PREAiClassification;
  onOverride: (id: string, purpose: MessagePurpose) => void;
  isUpdating?: boolean;
}

// ============================================
// Helpers
// ============================================

function getConfidenceColor(confidence: number): string {
  if (confidence >= 0.8) return 'bg-emerald-500';
  if (confidence >= 0.6) return 'bg-amber-500';
  return 'bg-red-500';
}

function getConfidenceTextColor(confidence: number): string {
  if (confidence >= 0.8) return 'text-emerald-600 dark:text-emerald-400';
  if (confidence >= 0.6) return 'text-amber-600 dark:text-amber-400';
  return 'text-red-600 dark:text-red-400';
}

function getOppositePurpose(purpose: MessagePurpose): MessagePurpose {
  return purpose === 'MARKETING' ? 'INFORMATIONAL' : 'MARKETING';
}

function formatDate(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// ============================================
// Component
// ============================================

export function AIClassificationReviewCard({
  classification,
  onOverride,
  isUpdating,
}: AIClassificationReviewCardProps) {
  const [isContentExpanded, setIsContentExpanded] = useState(false);
  const [isReasoningExpanded, setIsReasoningExpanded] = useState(false);

  const truncatedContent =
    classification.messageContent.length > 120
      ? classification.messageContent.slice(0, 120) + '...'
      : classification.messageContent;

  const needsTruncation = classification.messageContent.length > 120;
  const confidencePct = Math.round(classification.confidence * 100);

  return (
    <Card
      className={cn(
        'border-slate-200/60 dark:border-slate-700/60',
        'border-l-4 border-l-purple-500 dark:border-l-purple-400'
      )}
    >
      <CardContent className="p-4 space-y-3">
        {/* Message Content */}
        <div>
          <div className="flex items-start gap-2">
            <Brain className="w-4 h-4 text-purple-500 dark:text-purple-400 mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm text-slate-700 dark:text-slate-300">
                {isContentExpanded ? classification.messageContent : truncatedContent}
              </p>
              {needsTruncation && (
                <button
                  onClick={() => setIsContentExpanded(!isContentExpanded)}
                  className="text-xs text-purple-600 dark:text-purple-400 hover:underline mt-1 inline-flex items-center gap-0.5"
                >
                  {isContentExpanded ? 'Show less' : 'Show more'}
                  {isContentExpanded ? (
                    <ChevronUp className="w-3 h-3" />
                  ) : (
                    <ChevronDown className="w-3 h-3" />
                  )}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Classification + Confidence Row */}
        <div className="flex items-center gap-3 flex-wrap">
          {/* Purpose Badge */}
          <Badge
            variant={classification.classifiedPurpose === 'MARKETING' ? 'info' : 'success'}
            className="text-[10px]"
          >
            {classification.classifiedPurpose}
          </Badge>

          {/* Flagged Badge */}
          {classification.isFlagged && (
            <Badge variant="warning" className="text-[10px]">
              <Flag className="w-3 h-3 mr-1" />
              Flagged
            </Badge>
          )}

          {/* Confidence */}
          <div className="flex items-center gap-2">
            <div className="w-20 h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
              <div
                className={cn('h-full rounded-full transition-all', getConfidenceColor(classification.confidence))}
                style={{ width: `${confidencePct}%` }}
              />
            </div>
            <span className={cn('text-xs font-semibold tabular-nums', getConfidenceTextColor(classification.confidence))}>
              {confidencePct}%
            </span>
          </div>
        </div>

        {/* Reasoning (collapsible) */}
        {classification.reasoning && (
          <div>
            <button
              onClick={() => setIsReasoningExpanded(!isReasoningExpanded)}
              className="text-xs text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300 inline-flex items-center gap-1"
            >
              Reasoning
              {isReasoningExpanded ? (
                <ChevronUp className="w-3 h-3" />
              ) : (
                <ChevronDown className="w-3 h-3" />
              )}
            </button>
            {isReasoningExpanded && (
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 pl-4 border-l-2 border-slate-200 dark:border-slate-700">
                {classification.reasoning}
              </p>
            )}
          </div>
        )}

        {/* Override Section */}
        {classification.overriddenPurpose ? (
          <div className="flex items-center gap-2 flex-wrap text-xs">
            <span className="text-slate-500 dark:text-slate-400">Overridden:</span>
            <Badge
              variant={classification.classifiedPurpose === 'MARKETING' ? 'info' : 'success'}
              className="text-[10px]"
            >
              {classification.classifiedPurpose}
            </Badge>
            <ArrowRight className="w-3 h-3 text-slate-400" />
            <Badge
              variant={classification.overriddenPurpose === 'MARKETING' ? 'info' : 'success'}
              className="text-[10px]"
            >
              {classification.overriddenPurpose}
            </Badge>
            {classification.overriddenAt && (
              <span className="text-slate-400 dark:text-slate-500">
                {formatDate(classification.overriddenAt)}
              </span>
            )}
          </div>
        ) : classification.isFlagged ? (
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => onOverride(classification.id, classification.classifiedPurpose)}
              disabled={isUpdating}
              className="text-xs h-7"
            >
              {isUpdating ? (
                <Loader2 className="w-3 h-3 mr-1 animate-spin" />
              ) : (
                <Check className="w-3 h-3 mr-1" />
              )}
              Accept
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => onOverride(classification.id, getOppositePurpose(classification.classifiedPurpose))}
              disabled={isUpdating}
              className="text-xs h-7"
            >
              {isUpdating ? (
                <Loader2 className="w-3 h-3 mr-1 animate-spin" />
              ) : (
                <ArrowRight className="w-3 h-3 mr-1" />
              )}
              Override to {getOppositePurpose(classification.classifiedPurpose)}
            </Button>
          </div>
        ) : null}

        {/* Footer: Tenant + Timestamp */}
        <div className="flex items-center justify-between text-xs text-slate-400 dark:text-slate-500 pt-1 border-t border-slate-100 dark:border-slate-800">
          <span className="font-mono text-[10px] truncate max-w-[160px]" title={classification.tenantId}>
            {classification.tenantId}
          </span>
          <span>{formatDate(classification.createdAt)}</span>
        </div>
      </CardContent>
    </Card>
  );
}
